import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, Calendar } from "lucide-react";
import { POSTS } from "@/blog/posts";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";

const BLOG_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Blog",
  "name": "ResumeLab Blog",
  "description": "Data-driven resume insights from 120,000+ audits — ATS formatting, callback rates, and what actually gets recruiters to call.",
  "url": "https://resumelab.co.ke/blog",
  "blogPost": POSTS.map((p) => ({
    "@type": "BlogPosting",
    "headline": p.title,
    "datePublished": p.publishedAt,
    "url": `https://resumelab.co.ke/blog/${p.slug}`,
  })),
};

function PostCard({ post, featured = false }) {
  const pretty = new Date(post.publishedAt).toLocaleDateString("en-US", {
    year: "numeric", month: "short", day: "numeric",
  });
  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`blog-post-card-${post.slug}`}
      className={`group block bg-white border border-zinc-200 hover:border-zinc-900 transition-colors ${featured ? "lg:col-span-2" : ""}`}
    >
      <div className={`bg-gradient-to-br ${post.heroAccent} ${featured ? "p-10 lg:p-14" : "p-8"}`}>
        <div className="font-mono text-[10px] uppercase tracking-widest text-white/70">◷ {post.tags[0]}</div>
        <div className={`mt-4 font-display font-extrabold text-white tracking-tight leading-[1.05] ${featured ? "text-3xl lg:text-5xl" : "text-2xl"}`}>
          {post.title}
        </div>
        {featured && post.hero?.stat ? (
          <div className="mt-8 inline-flex items-baseline gap-3 bg-white/10 backdrop-blur-sm px-4 py-3 border border-white/20">
            <span className="font-display font-extrabold text-3xl text-white">{post.hero.stat}</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-white/80 max-w-[180px]">{post.hero.statLabel}</span>
          </div>
        ) : null}
      </div>
      <div className="p-6 lg:p-8">
        <p className="text-zinc-600 text-sm leading-relaxed">{post.description}</p>
        <div className="mt-5 pt-5 border-t border-zinc-100 flex items-center justify-between text-xs text-zinc-500">
          <div className="flex items-center gap-4 font-mono uppercase tracking-wider">
            <span className="inline-flex items-center gap-1.5"><Calendar className="w-3 h-3" />{pretty}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="w-3 h-3" />{post.readingMinutes} MIN READ</span>
          </div>
          <span className="inline-flex items-center gap-1 text-zinc-900 font-medium group-hover:text-[#EF4444] transition-colors">
            Read <ArrowUpRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function BlogIndex() {
  // Sort by publishedAt desc; newest = featured (2-col), rest in standard grid
  const sorted = [...POSTS].sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
  const [featured, ...rest] = sorted;
  return (
    <main className="text-zinc-900 min-h-screen bg-gradient-to-br from-blue-50 via-fuchsia-50 to-amber-50" data-testid="blog-index">
      <SEO
        title="Blog — Data-driven resume insights from 120,000+ audits"
        description="Resume formatting, ATS compatibility, and callback-rate research from ResumeLab's 120,000+ audits. Stop guessing, start landing."
        path="/blog"
        jsonLd={BLOG_JSONLD}
      />
      <section className="border-b border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-12 lg:pb-16">
          <div className="overline mb-4">◇ FIELD NOTES</div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95] max-w-3xl">
            Resume insights<br />from <span className="brand-red">120,000+ audits.</span>
          </h1>
          <p className="mt-6 text-zinc-600 text-base lg:text-lg max-w-2xl leading-relaxed">
            Data-driven posts on ATS formatting, callback rates, and the small changes that move scores the most. Built from what we see across actual job-search outcomes — not opinions.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-8 lg:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {featured ? <PostCard post={featured} featured /> : null}
          {rest.map((p) => (
            <PostCard key={p.slug} post={p} />
          ))}
        </div>

        <div className="mt-16 pt-12 border-t border-zinc-200 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-3">▲ TRY IT</div>
            <h2 className="font-display font-extrabold text-2xl lg:text-3xl leading-tight">Run your resume through the same audit our data is built on.</h2>
          </div>
          <div className="lg:col-span-5 lg:text-right">
            <Link
              to="/evaluate"
              data-testid="blog-cta-evaluate"
              className="btn-sharp bg-[#DC1C22] hover:bg-[#A8141A] text-white"
            >
              Score my resume free <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
